import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import {
  filterValuesSeparator,
  filterValuesSeparatorSplitRegex
} from 'shared-helpers/consts';
import { BoxTableCell } from 'components/common/box';
import {
  CoursePathDeadlines,
  CourseUserDeadline
} from 'components/courses/common';
import {
  AnswersProgress,
  CompletionStatus,
  ReadingProgress,
  TaskPointsProgress,
  VideoProgress
} from 'components/common/tasks';

import type { PageState } from 'types';

interface Props {
  returnPart: 'header' | 'body';
  user?: PageState['user'];
}

const UsersViewCourse = ({ returnPart, user }: Props) => {
  const {
    messages,
    pageState: { usersCourseData }
  } = useReduxState();

  const router = useRouter();

  const courseData =
    usersCourseData?.[
      (router?.query?.view as string)
        ?.split(filterValuesSeparatorSplitRegex)
        ?.slice(1)
        .join(filterValuesSeparator)
    ]?.[user?.id];

  return returnPart === 'header' ? (
    <>
      <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
      <BoxTableCell>{messages.tasksReading}</BoxTableCell>
      <BoxTableCell>{messages.tasksVideos}</BoxTableCell>
      <BoxTableCell>{messages.tasksAnswers}</BoxTableCell>
      <BoxTableCell>{messages.tasksTaskPoints}</BoxTableCell>
      <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
    </>
  ) : returnPart === 'body' && courseData ? (
    <>
      <BoxTableCell label={messages.tasksReading}>
        {courseData.deadline ? (
          <CourseUserDeadline
            deadline={courseData.deadline}
            isActive={courseData.completion?.status !== 'success'}
            isEdited={Boolean(user.courseSettings?.deadlineType)}
            showIcon={true}
          />
        ) : courseData.relatedPathDeadlines?.length ? (
          <CoursePathDeadlines
            courseId={courseData.id}
            isActive={courseData.completion?.status !== 'success'}
            pathDeadlines={courseData.relatedPathDeadlines}
            userId={user.id}
          />
        ) : null}
      </BoxTableCell>
      <BoxTableCell label={messages.tasksReading}>
        <ReadingProgress
          analyticsDisabledForAdmin={courseData.analyticsDisabledForAdmin}
          discrepancyInCompletion={
            courseData.discrepancyInCompletion?.readingProgress
          }
          readingProgress={courseData.readingProgress}
        />
      </BoxTableCell>

      <BoxTableCell label={messages.tasksVideos}>
        <VideoProgress
          analyticsDisabledForAdmin={courseData.analyticsDisabledForAdmin}
          discrepancyInCompletion={
            courseData.discrepancyInCompletion?.videoProgress
          }
          numberOfVideos={courseData.numberOfVideos}
          videoProgress={courseData.videoProgress}
          videosWatched={courseData.videoProgressVideosWatched}
        />
      </BoxTableCell>

      <BoxTableCell label={messages.tasksAnswers}>
        <AnswersProgress
          numberOfAnswers={courseData.numberOfAnswers}
          numberOfTasks={courseData.numberOfTasks}
        />
      </BoxTableCell>

      <BoxTableCell label={messages.tasksTaskPoints}>
        <TaskPointsProgress
          discrepancyInCompletion={
            courseData.discrepancyInCompletion?.taskPointsProgress
          }
          numberOfTasks={courseData.numberOfTasks}
          taskMaxPoints={courseData.taskMaxPoints}
          taskPoints={courseData.taskPoints}
          taskPointsProgress={courseData.taskPointsProgress}
        />
      </BoxTableCell>

      <BoxTableCell label={messages.commonCompleted}>
        <CompletionStatus
          completion={courseData.completion}
          discrepancyInCompletion={Boolean(courseData.discrepancyInCompletion)}
          progressData={courseData}
        />
      </BoxTableCell>
    </>
  ) : null;
};

export default UsersViewCourse;
